import * as Yup from 'yup';
import {ApplicationCategory, ApplicationStatus} from '../types';

// FNP-79: when `provisionAutomatically` is on, the create kicks off the
// FNP-60 programmatic-provisioning state machine. The backend validates the
// name format (^[a-z][a-z0-9-]*$) and uniqueness in that flow; the form
// mirrors the format check for instant feedback.
const PROGRAMMATIC_NAME_REGEX = /^[a-z][a-z0-9-]*$/;

// FNP-287: subdomain shares the name's hostname-label charset. Same regex
// works because the field is also a single hostname label (no dots).
const SUBDOMAIN_REGEX = /^[a-z][a-z0-9-]*$/;

// FNP-1489: a hosted zone apex — at least two dot-separated labels, no scheme,
// no trailing dot. Route 53 reports zone names WITH a trailing dot; the form
// takes the bare name because that is what `composeAppDomain` concatenates.
const ZONE_NAME_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

// ⚠️ PINNED TO us-east-1 DELIBERATELY. CloudFront accepts certificates from
// that region only, and ACM certificates cannot be shared across accounts for
// CloudFront — so a wrong-region ARN synthesizes cleanly and fails at the
// provisioned application's CloudFormation deploy, after both repositories and
// both pipelines exist. The account half cannot be checked from here.
const CERTIFICATE_ARN_REGEX = /^arn:aws:acm:us-east-1:\d{12}:certificate\/[0-9a-f-]+$/;

// FNP-1489: the three addressing fields are supplied together or not at all.
//
// ⚠️ A PARTIAL SET IS NOT A CONVENIENCE, IT IS A SILENT WRONG ANSWER. Supplying
// only some leaves the orchestrator's `isFullySupplied` false, so it runs the
// platform lookup for the rest — resolving a different zone, in a different
// account, while the admin believes their own was used. Every downstream signal
// then reports success.
//
// Reads all three off `this.parent`, which Yup populates with the whole sibling
// object INCLUDING the field currently under test — so counting `value`
// separately would double-count it and let two filled fields look like three.
const addressingIsConsistent = (
    parent: {hostedZoneId?: string, hostedZoneName?: string, certificateArn?: string},
): boolean => {
    const filled = [parent?.hostedZoneId, parent?.hostedZoneName, parent?.certificateArn]
        .filter((v) => typeof v === 'string' && v.trim() !== '').length;
    return filled === 0 || filled === 3;
};

export const RecordSchema = Yup.object()
                               .shape({
                                          // FNP-1498: THE NAME RULE IS
                                          // UNCONDITIONAL NOW.
                                          //
                                          // It used to apply only when the
                                          // `provisionAutomatically` toggle was
                                          // on — and that toggle defaulted OFF.
                                          // So the form happily accepted
                                          // `Some Name With Spaces` and the
                                          // backend then refused it, because
                                          // console-api's create resolver
                                          // checks the format on EVERY path.
                                          // Two rules, one of them optional,
                                          // for one constraint.
                                          //
                                          // The toggle is gone with the
                                          // provisioning fieldset, so this is
                                          // now simply the rule.
                                          name: Yup.string()
                                                   .required('Required')
                                                   .matches(
                                                       PROGRAMMATIC_NAME_REGEX,
                                                       'Must be lowercase, start with a letter, and contain only letters, digits, and hyphens',
                                                   )
                                                   // The ceiling comes from the
                                                   // S3 deployment bucket:
                                                   // `<name>-infra-<accountId>-ui`
                                                   // adds 22 characters against
                                                   // S3's limit of 63. Nothing
                                                   // enforced it before, and
                                                   // exceeding it failed inside
                                                   // the PROVISIONED app's own
                                                   // pipeline — two repos and
                                                   // two pipelines after the
                                                   // mistake. See CONVENTIONS.md.
                                                   .max(41, 'At most 41 characters — longer names break the deployment bucket name'),

                                          description: Yup.string()
                                                          .required('Required'),

                                          // FNP-279: applicationCategory must
                                          // be set so the list table doesn't
                                          // render "Invalid Category" badges.
                                          applicationCategory: Yup.string()
                                                                  .oneOf(Object.values(ApplicationCategory))
                                                                  .required('Please select a category'),

                                          applicationStatus: Yup.string()
                                                                .oneOf(Object.values(ApplicationStatus))
                                                                .required('Please select a status'),

                                      });

// FNP-1498/FNP-1500: THE PROVISIONING RULES, which are no longer part of the
// record form.
//
// ⚠️ THESE ARE NOT DEAD CODE. They moved here whole, out of RecordSchema,
// because the fields they validate moved out of the create form — provisioning
// is its own act with its own form. Deleting them would throw away rules that
// each exist because of a specific failure:
//
//   subdomain      — an empty one claims the zone APEX, which is a real
//                    FNP-287 feature and almost always a mistake
//   all-or-none    — a partial set makes the orchestrator look up whatever was
//                    omitted, resolving a DIFFERENT zone in a different account
//                    while the operator believes theirs was used
//   us-east-1      — CloudFront accepts no other region and ACM certificates
//                    cannot be shared cross-account, so a wrong-region ARN
//                    fails at the provisioned app's CloudFormation deploy
//   zone name      — rejects a URL and the trailing dot Route 53 displays,
//                    which is the likeliest paste error
//
// FNP-1500 wires this into the provisioning form.
export const ProvisioningSchema = Yup.object()
                               .shape({
                                          // FNP-287: optional subdomain (a
                                          // single hostname label). Empty
                                          // means apex deployment under the
                                          // parent domain. Validated only
                                          // when provisionAutomatically is
                                          // on; the backend mirrors the
                                          // same shape.
                                          subdomain: Yup.string()
                                                        .nullable()
                                                        .notRequired()
                                                        .test(
                                                            'subdomain-format',
                                                            'Must be a single hostname label: lowercase, start with a letter, only letters/digits/hyphens (no dots).',
                                                            (value) => !value || SUBDOMAIN_REGEX.test(value),
                                                        ),

                                          // FNP-1489: the supplied addressing facts.
                                          //
                                          // ⚠️ ALL THREE OR NONE, checked here so the
                                          // admin is told at the form instead of by a
                                          // refusal from the orchestrator minutes later.
                                          // The orchestrator enforces the same rule — this
                                          // is a convenience, not the gate, and the two
                                          // must not drift.
                                          //
                                          // Blank is the normal case: it means "look the
                                          // zone up", which is what every flexi.network
                                          // application does.
                                          hostedZoneId: Yup.string()
                                                           .nullable()
                                                           .notRequired()
                                                           .test(
                                                               'addressing-all-or-none',
                                                               'Supply the zone ID, zone name and certificate ARN together, or leave all three blank to look the zone up.',
                                                               function () {
                                                                   return addressingIsConsistent(this.parent);
                                                               },
                                                           ),

                                          hostedZoneName: Yup.string()
                                                             .nullable()
                                                             .notRequired()
                                                             .test(
                                                                 'addressing-all-or-none',
                                                                 'Supply the zone ID, zone name and certificate ARN together, or leave all three blank to look the zone up.',
                                                                 function () {
                                                                     return addressingIsConsistent(this.parent);
                                                                 },
                                                             )
                                                             .test(
                                                                 'zone-name-format',
                                                                 'Must be a domain name, e.g. "testing.flexi.network" — not a URL and no trailing dot.',
                                                                 (value) => !value || ZONE_NAME_REGEX.test(value),
                                                             ),

                                          certificateArn: Yup.string()
                                                             .nullable()
                                                             .notRequired()
                                                             .test(
                                                                 'addressing-all-or-none',
                                                                 'Supply the zone ID, zone name and certificate ARN together, or leave all three blank to look the zone up.',
                                                                 function () {
                                                                     return addressingIsConsistent(this.parent);
                                                                 },
                                                             )
                                                             // ⚠️ REGION IS PART OF THE CONTRACT, not cosmetic.
                                                             // CloudFront only accepts a certificate from
                                                             // us-east-1, and it cannot be shared across
                                                             // accounts — so a cert from another region fails
                                                             // at the provisioned app's CloudFormation deploy,
                                                             // long after the repos exist. The account cannot
                                                             // be checked here, but the region can.
                                                             .test(
                                                                 'certificate-arn-format',
                                                                 'Must be an ACM certificate ARN in us-east-1 — CloudFront accepts no other region.',
                                                                 (value) => !value || CERTIFICATE_ARN_REGEX.test(value),
                                                             ),
                                      });
